import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Images, LayoutGrid, List } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { DatePicker } from '@/components/ui/date-picker';
import { FilterBar } from '@/components/shared/filter-bar';
import { Pagination } from '@/components/shared/pagination';
import { PermissionGate } from '@/components/shared/permission-gate';
import { EmptyState } from '@/components/shared/states';
import { SearchInput } from '@/components/shared/search-input';
import { ContentPageHeader } from '@/pages/content/content-page-header';
import { useContentPeriodStore } from '@/pages/content/content-period-store';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toast';
import {
  GalleryCategoriesDialog,
  type GalleryCategory,
} from '@/pages/content/gallery-categories-dialog';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { PERMISSIONS } from '@/lib/permissions';
import { usePreferences } from '@/stores/preferences-store';
import { formatDate, formatDateTime } from '@/utils/format';
import { cn } from '@/utils/cn';

interface Gallery {
  id: string;
  title: string;
  description: string | null;
  categoryId: string | null;
  categoryName: string | null;
  status: 'draft' | 'scheduled' | 'published';
  publishAt: string | null;
  publishedAt: string | null;
  coverPhotoId: string | null;
  photoCount: number;
  coverThumbnailUrl: string | null;
  coverMainColor: string | null;
  coverAspectRatio: number | null;
  publishedOn: string;
  createdAt: string;
  canDelete?: boolean;
}

const publishDisplay = (row: Gallery): string | null => row.publishAt ?? row.publishedAt;

type GalleryView = 'grid' | 'list';
type VisibilityFilter = 'all' | 'in_app' | 'not_in_app';

const PAGE_SIZE = 24;
const VISIBILITY_ANY: VisibilityFilter = 'all';
const CATEGORY_ANY = '__all__';

const GalleryCover = ({
  gallery,
  className,
  iconClassName,
  showPublicBadge = false,
}: {
  gallery: Gallery;
  className?: string;
  iconClassName?: string;
  showPublicBadge?: boolean;
}) => {
  const { t } = useTranslation('content');

  return (
  <div
    className={cn('relative overflow-hidden bg-surface-muted', className)}
    style={{ backgroundColor: gallery.coverMainColor ?? undefined }}
  >
    {gallery.coverThumbnailUrl ? (
      <img
        src={gallery.coverThumbnailUrl}
        alt=""
        className="h-full w-full object-cover"
        loading="lazy"
      />
    ) : (
      <div className="flex h-full items-center justify-center text-fg-tertiary">
        <Images className={cn('opacity-50', iconClassName ?? 'size-5')} aria-hidden />
      </div>
    )}
    {gallery.status !== 'published' ? (
      <div className="absolute inset-0 bg-background/30" aria-hidden />
    ) : null}
    {showPublicBadge && gallery.status === 'published' ? (
      <Badge variant="success" className="absolute start-1.5 top-1.5 max-w-[calc(100%-0.75rem)] truncate shadow-sm">
        {t('galleries.status.published')}
      </Badge>
    ) : null}
  </div>
  );
};

export const GalleriesPage = () => {
  const { t } = useTranslation('content');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const timezone = usePreferences((state) => state.timezone);

  const [open, setOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [publishedOn, setPublishedOn] = useState(() =>
    DateTime.now().setZone(timezone).toFormat('yyyy-MM-dd'),
  );
  const [page, setPage] = useState(1);
  const [view, setView] = useState<GalleryView>('grid');
  const [search, setSearch] = useState('');
  const [visibility, setVisibility] = useState<VisibilityFilter>(VISIBILITY_ANY);
  const [categoryFilter, setCategoryFilter] = useState(CATEGORY_ANY);
  const [publishTarget, setPublishTarget] = useState<Gallery | null>(null);
  const [unpublishTarget, setUnpublishTarget] = useState<Gallery | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Gallery | null>(null);
  const dateRange = useContentPeriodStore((state) => state.dateRange);

  useEffect(() => {
    setPage(1);
  }, [dateRange.from, dateRange.to]);

  const appliedCount = useMemo(() => {
    let count = 0;
    if (search.trim()) count += 1;
    if (visibility !== VISIBILITY_ANY) count += 1;
    if (categoryFilter !== CATEGORY_ANY) count += 1;
    return count;
  }, [search, visibility, categoryFilter]);

  const categories = useQuery({
    queryKey: ['content', 'gallery-categories'],
    queryFn: () =>
      api.get<{ data: GalleryCategory[] }>('/content/gallery-categories', {
        query: { page: 1, pageSize: 100 },
      }),
  });

  const categoryList = categories.data?.data ?? [];

  const list = useQuery({
    queryKey: [
      'content',
      'galleries',
      page,
      search,
      visibility,
      categoryFilter,
      dateRange.from,
      dateRange.to,
    ],
    queryFn: () =>
      api.get<{ data: Gallery[]; page: { totalItems: number; totalPages: number } }>(
        '/content/galleries',
        {
          query: {
            page,
            pageSize: PAGE_SIZE,
            q: search.trim() || undefined,
            categoryId: categoryFilter === CATEGORY_ANY ? undefined : categoryFilter,
            visibility: visibility === VISIBILITY_ANY ? undefined : visibility,
            dateFrom: dateRange.from ?? undefined,
            dateTo: dateRange.to ?? undefined,
          },
        },
      ),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<Gallery>('/content/galleries', {
        title,
        publishedOn,
        categoryId: categoryId || null,
      }),
    onSuccess: (gallery) => {
      toast.success(t('galleries.saved'));
      setOpen(false);
      setTitle('');
      setCategoryId(categoryList[0]?.id ?? '');
      setPublishedOn(DateTime.now().setZone(timezone).toFormat('yyyy-MM-dd'));
      void queryClient.invalidateQueries({ queryKey: ['content', 'galleries'] });
      void navigate(`/content/galleries/${gallery.id}`);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const publish = useMutation({
    mutationFn: (id: string) => api.post<Gallery>(`/content/galleries/${id}/publish`),
    onSuccess: () => {
      toast.success(t('galleries.published'));
      setPublishTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['content', 'galleries'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const unpublish = useMutation({
    mutationFn: (id: string) => api.post<Gallery>(`/content/galleries/${id}/unpublish`),
    onSuccess: () => {
      toast.success(t('galleries.unpublished'));
      setUnpublishTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['content', 'galleries'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/content/galleries/${id}`),
    onSuccess: () => {
      toast.success(t('galleries.deleted'));
      setDeleteTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['content', 'galleries'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const rows = list.data?.data ?? [];
  const hasFilters = appliedCount > 0;

  const columns: DataTableColumn<Gallery>[] = [
    {
      id: 'publishedOn',
      header: t('galleries.columns.publishedOn'),
      cell: (row) => formatDate(row.publishedOn),
    },
    {
      id: 'publishAt',
      header: t('galleries.columns.publishAt'),
      cell: (row) => {
        const value = publishDisplay(row);
        return value ? formatDateTime(value) : '—';
      },
    },
    {
      id: 'title',
      header: t('galleries.columns.title'),
      cell: (row) => (
        <div className="flex items-center gap-3">
          <GalleryCover gallery={row} className="size-10 shrink-0 rounded-md" iconClassName="size-4" />
          <div className="min-w-0">
            <span className="block truncate font-medium text-fg">{row.title}</span>
            {row.categoryName ? (
              <span className="block truncate text-caption text-fg-secondary">{row.categoryName}</span>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      id: 'category',
      header: t('galleries.columns.category'),
      cell: (row) => row.categoryName ?? '—',
      hideBelowMd: true,
    },
    {
      id: 'photos',
      header: t('galleries.columns.photos'),
      cell: (row) => t('galleries.photoCount', { count: row.photoCount }),
      hideBelowMd: true,
    },
    {
      id: 'status',
      header: t('galleries.columns.status'),
      cell: (row) => (
        <Badge variant={row.status === 'published' ? 'success' : 'neutral'}>
          {row.status === 'published'
            ? t('galleries.status.published')
            : t(`status.${row.status}`)}
        </Badge>
      ),
    },
  ];

  const galleryRowActions = (row: Gallery) => (
    <div className="flex justify-end gap-2">
      {row.status === 'published' ? (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setUnpublishTarget(row)}
        >
          {t('galleries.unpublish')}
        </Button>
      ) : (
        <Button size="sm" onClick={() => setPublishTarget(row)}>
          {t('galleries.publish')}
        </Button>
      )}
      {row.canDelete === true ? (
        <PermissionGate permission={PERMISSIONS.CONTENT_DELETE}>
          <Button size="sm" variant="outline" onClick={() => setDeleteTarget(row)}>
            {tCommon('action.delete')}
          </Button>
        </PermissionGate>
      ) : null}
    </div>
  );

  const clearFilters = () => {
    setSearch('');
    setVisibility(VISIBILITY_ANY);
    setCategoryFilter(CATEGORY_ANY);
    setPage(1);
  };

  const openCreateDialog = () => {
    if (!categoryId && categoryList[0]) {
      setCategoryId(categoryList[0].id);
    }
    setOpen(true);
  };

  return (
    <div>
      <ContentPageHeader
        actions={
          <>
            <Button variant="outline" onClick={() => setCategoriesOpen(true)}>
              {t('galleries.categories.manage')}
            </Button>
            <Button onClick={openCreateDialog}>{t('galleries.create')}</Button>
          </>
        }
      />

      <FilterBar
        appliedCount={appliedCount}
        onClearAll={clearFilters}
        labels={{
          filters: t('galleries.filters.title'),
          clearAll: t('galleries.filters.clearAll'),
          applied: (count) => t('galleries.filters.applied', { count }),
        }}
        leading={
          <SearchInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            placeholder={t('galleries.filters.search')}
            clearLabel={tCommon('search.clear')}
            className="max-w-none sm:w-56"
            aria-label={t('galleries.filters.search')}
          />
        }
        trailing={
          <div
            className="flex rounded-lg border border-border p-0.5"
            role="group"
            aria-label={t('galleries.view.label')}
          >
            <Button
              type="button"
              size="sm"
              variant={view === 'grid' ? 'primary' : 'ghost'}
              aria-pressed={view === 'grid'}
              aria-label={t('galleries.view.grid')}
              onClick={() => setView('grid')}
            >
              <LayoutGrid className="size-4" aria-hidden />
            </Button>
            <Button
              type="button"
              size="sm"
              variant={view === 'list' ? 'primary' : 'ghost'}
              aria-pressed={view === 'list'}
              aria-label={t('galleries.view.list')}
              onClick={() => setView('list')}
            >
              <List className="size-4" aria-hidden />
            </Button>
          </div>
        }
      >
        {({ popoverPortal }) => (
          <>
            <div className="w-44 min-w-[10rem]">
              <Listbox
                value={categoryFilter}
                onChange={(value) => {
                  setCategoryFilter(value ?? CATEGORY_ANY);
                  setPage(1);
                }}
                options={[
                  { value: CATEGORY_ANY, label: t('galleries.filters.categoryAny') },
                  ...categoryList.map((item) => ({ value: item.id, label: item.name })),
                ]}
                popoverPortal={popoverPortal}
              />
            </div>
            <div className="w-44 min-w-[10rem]">
              <Listbox
                value={visibility}
                onChange={(value) => {
                  setVisibility((value ?? VISIBILITY_ANY) as VisibilityFilter);
                  setPage(1);
                }}
                options={[
                  { value: VISIBILITY_ANY, label: t('galleries.filters.visibilityAny') },
                  { value: 'in_app', label: t('galleries.filters.visibilityInApp') },
                  { value: 'not_in_app', label: t('galleries.filters.visibilityNotInApp') },
                ]}
                popoverPortal={popoverPortal}
              />
            </div>
          </>
        )}
      </FilterBar>

      {list.isLoading ? (
        view === 'grid' ? (
          <ul className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
            {Array.from({ length: 12 }).map((_, index) => (
              <li key={index}>
                <Card className="overflow-hidden p-0">
                  <Skeleton className="aspect-square rounded-none" />
                  <div className="space-y-1.5 p-2">
                    <Skeleton className="h-3.5 w-3/4" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        ) : (
          <DataTable rows={[]} columns={columns} rowKey={() => ''} loading />
        )
      ) : rows.length === 0 ? (
        hasFilters ? (
          <EmptyState
            title={tCommon('state.noFilterResults')}
            body={tCommon('state.noFilterResultsBody')}
            action={
              <Button variant="outline" onClick={clearFilters}>
                {t('galleries.filters.clearAll')}
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={<Images className="size-6" aria-hidden />}
            title={t('galleries.emptyTitle')}
            body={t('galleries.empty')}
            action={<Button onClick={openCreateDialog}>{t('galleries.create')}</Button>}
          />
        )
      ) : view === 'list' ? (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(row) => row.id}
          onRowClick={(row) => void navigate(`/content/galleries/${row.id}`)}
          rowActions={galleryRowActions}
          actionsHeader={tCommon('table.actions')}
        />
      ) : (
        <ul className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
          {rows.map((gallery) => {
            const meta = gallery.categoryName
              ? t('galleries.listMetaWithCategory', {
                  date: formatDate(gallery.publishedOn),
                  count: gallery.photoCount,
                  category: gallery.categoryName,
                })
              : t('galleries.listMeta', {
                  date: formatDate(gallery.publishedOn),
                  count: gallery.photoCount,
                });
            return (
              <li key={gallery.id}>
                <button
                  type="button"
                  className="group block w-full text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-lg"
                  onClick={() => void navigate(`/content/galleries/${gallery.id}`)}
                >
                  <Card className="overflow-hidden p-0 transition-shadow group-hover:shadow-e2">
                    <GalleryCover gallery={gallery} className="aspect-square w-full" showPublicBadge />
                    <div className="border-t border-border px-2 py-1.5">
                      <p className="truncate text-body-sm font-medium text-fg">{gallery.title}</p>
                      <p className="truncate text-caption text-fg-secondary">{meta}</p>
                    </div>
                  </Card>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {!list.isLoading && list.data?.page && list.data.page.totalPages > 1 ? (
        <div className="mt-6">
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            totalItems={list.data.page.totalItems}
            totalPages={list.data.page.totalPages}
            onPageChange={setPage}
            labels={{
              showing: ({ from, to, total }) =>
                tCommon('pagination.showing', { from, to, total }),
              perPage: tCommon('pagination.perPage'),
              first: tCommon('pagination.first'),
              previous: tCommon('pagination.previous'),
              next: tCommon('pagination.next'),
              last: tCommon('pagination.last'),
            }}
          />
        </div>
      ) : null}

      <ConfirmDialog
        open={publishTarget !== null}
        onOpenChange={(next) => {
          if (!next) setPublishTarget(null);
        }}
        title={t('confirm.publishGallery.title', { name: publishTarget?.title ?? '' })}
        description={t('confirm.publishGallery.description')}
        confirmLabel={t('confirm.publishGallery.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={publish.isPending}
        onConfirm={() => {
          if (publishTarget) publish.mutate(publishTarget.id);
        }}
      />

      <ConfirmDialog
        open={unpublishTarget !== null}
        onOpenChange={(next) => {
          if (!next) setUnpublishTarget(null);
        }}
        title={t('confirm.unpublishGallery.title', { name: unpublishTarget?.title ?? '' })}
        description={t('confirm.unpublishGallery.description')}
        confirmLabel={t('confirm.unpublishGallery.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={unpublish.isPending}
        onConfirm={() => {
          if (unpublishTarget) unpublish.mutate(unpublishTarget.id);
        }}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(next) => {
          if (!next) setDeleteTarget(null);
        }}
        title={t('confirm.deleteGallery.title', { name: deleteTarget?.title ?? '' })}
        description={t('confirm.deleteGallery.description')}
        confirmLabel={t('confirm.deleteGallery.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={remove.isPending}
        onConfirm={() => {
          if (deleteTarget) remove.mutate(deleteTarget.id);
        }}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('galleries.create')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="gallery-title">{t('galleries.titleLabel')}</Label>
              <Input id="gallery-title" value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="gallery-category">{t('galleries.categoryLabel')}</Label>
              <Listbox
                id="gallery-category"
                value={categoryId}
                onChange={(value) => setCategoryId(value ?? '')}
                options={categoryList.map((item) => ({ value: item.id, label: item.name }))}
                popoverPortal="dialog"
              />
            </div>
            <div>
              <Label htmlFor="gallery-published-on">{t('galleries.publishedOnLabel')}</Label>
              <DatePicker
                id="gallery-published-on"
                value={publishedOn}
                onChange={(date) => date && setPublishedOn(date)}
                placeholder={t('galleries.publishedOnLabel')}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button
                disabled={!title.trim() || !publishedOn || create.isPending}
                onClick={() => create.mutate()}
              >
                {tCommon('action.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <GalleryCategoriesDialog open={categoriesOpen} onOpenChange={setCategoriesOpen} />
    </div>
  );
};
